import {
  useShow,
  useOne,
  useMany,
  useGetIdentity,
  useNavigation,
  useList,
  useCreate,
  useInvalidate,
  useNotification,
} from '@refinedev/core'
import InfoIcon from '@mui/icons-material/Info'

import { Show } from '@refinedev/mui'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import FunderOpportunitiesList from '../../components/FunderOpportunitiesList'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import FunderRegionManagement from '../../components/ManageFunderRegions'
import { useState } from 'react'
import FunderRevisions from '../../components/FunderRevisions'
import ReactMarkdown from 'react-markdown'
import AdminComments from '../../components/AdminComments'
import { ProposedEditsPanel } from '../proposed-edits/ProposedEditsPanel'
import AssignUserFunderModal from '../../components/AssignUserFunderModal'
import { User } from '@/utils/auth/auth0'
import UpdateProfilesModal from '../../components/UpdateProfilesModal'
import { UUID } from 'crypto'
import React from 'react'
import remarkGfm from 'remark-gfm'
import { getSixMonthsFromNow } from '@/utils/dataUtils'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'

interface Funder {
  uuid: string
  name: string
  type: string | null
  active: boolean
  status: string | null
  admin_comments: string | null
  description: string | null
  website: string | null
  notes: string | null
  only_funds_registered_charities: boolean | null
  gambling_proceeds: boolean | null
  is_international: boolean | null
  iwi: number[]
}

export default function FunderShow() {
  const { query } = useShow({
    errorNotification: (error) => {
      return {
        message: 'Error getting funder',
        description:
          error?.message || 'An error occurred while fetching funder',
        type: 'error',
      }
    },
  })
  const { data, isFetching, isError } = query
  const { data: user } = useGetIdentity() as User
  const { show } = useNavigation()

  const { mutate: createOpportunity } = useCreate()
  const invalidate = useInvalidate()
  const { open: openNotification } = useNotification()
  const [newFundName, setNewFundName] = useState('')
  const [isCreatingOpportunity, setIsCreatingOpportunity] = useState(false)
  const [addMode, setAddMode] = useState<'name' | 'references'>('name')
  const [newReferences, setNewReferences] = useState('')
  const navigate = useNavigate()

  const [tabValue, setTabValue] = useState(0)
  const [editingRegions, setEditingRegions] = useState(false)
  const [addingOpportunity, setAddingOpportunity] = useState(false)
  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false)

  function handleAddOpportunity() {
    if (!data?.data.uuid || !newFundName.trim()) return
    setIsCreatingOpportunity(true)
    createOpportunity(
      {
        resource: 'opportunities',
        values: {
          funder_uuid: data.data.uuid,
          fund_name: newFundName.trim(),
          flag_for_updates: 'Needs Work',
          active: true,
          is_international: data.data.is_international ?? false,
          gambling_proceeds: data.data.gambling_proceeds ?? false,
          only_funds_registered_charities:
            data.data.only_funds_registered_charities ?? false,
          // Compute in local TZ so the date reflects the user's "today", not the UTC server's
          next_check_in_date: format(getSixMonthsFromNow(), 'yyyy-MM-dd'),
        },
        meta: {
          updated_by:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
        },
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setIsCreatingOpportunity(false)
          setNewFundName('')
          setAddingOpportunity(false)
          invalidate({
            resource: 'funder',
            id: data.data.uuid,
            invalidates: ['detail'],
          })
        },
        onError: (error) => {
          setIsCreatingOpportunity(false)
          const detail = (error as any)?.response?.data?.detail || ''
          const message = detail.includes('unique_fund_name_funder')
            ? 'An opportunity with this name already exists for this funder.'
            : detail ||
              (error as any)?.message ||
              'Error creating opportunity. Please try again.'
          openNotification?.({ message, type: 'error' })
        },
      },
    )
  }
  const [openUpdateProfilesModal, setOpenUpdateProfilesModal] = useState(false)
  const [opportunityUUIDsToUpdate, setOpportunityUUIDsToUpdate] = useState<
    UUID[]
  >([])

  const theme = useTheme()
  const lightOrDarkMode = theme.palette.mode

  interface FunderRegion {
    funder_uuid: number
    region_id: number
  }

  function a11yProps(index: number) {
    return {
      id: `funder-tab-${index}`,
      'aria-controls': `funder-tabpanel-${index}`,
    }
  }

  function handleTabChange(e: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue)
  }

  function toggleRegionsEdit() {
    setEditingRegions(!editingRegions)
  }

  function toggleAddingOpportunity() {
    setAddingOpportunity(!addingOpportunity)
    setAddMode('name')
    setNewFundName('')
    setNewReferences('')
  }

  function handleOpenCreateWithReferences() {
    if (!data?.data.uuid || !newReferences.trim()) return
    navigate('/opportunities/create', {
      state: {
        autoFillFromReferences: {
          funder_uuid: data.data.uuid,
          references: newReferences.trim(),
        },
      },
    })
  }

  function toggleAssignUserEdit() {
    setAssignUserModalOpen(true)
  }

  const { data: regionData, isLoading: regionDataIsLoading } = useOne({
    resource: 'funder-regions',
    id: data?.data.uuid,
    queryOptions: {
      enabled: !!data?.data.uuid,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting regions for this funder',
        description:
          error?.message ||
          'An error occurred while fetching the Funder Regions',
        type: 'error',
      }
    },
  })

  const { data: funderAssignedTo, isLoading: funderAssignedToIsLoading } =
    useOne({
      resource: 'assigned-funders',
      id: data?.data.uuid,
      queryOptions: {
        enabled: !!data?.data.uuid,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting funder assignee',
          description:
            error?.message ||
            'An error occurred while fetching the funder assignee',
          type: 'error',
        }
      },
    })

  const { data: regionNamesData, isLoading: regionNameIsLoading } = useMany({
    resource: 'regions',
    ids: regionData?.data.map((r: FunderRegion) => r.region_id) || [],
    queryOptions: {
      enabled:
        regionData?.data && regionData?.data.length > 0 && !regionDataIsLoading,
    },
    meta: {
      paginate: {
        pageSize: 100,
      },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting region names',
        description:
          error?.message || 'An error occurred while fetching region names',
        type: 'error',
      }
    },
  })

  const { data: iwiNamesData, isLoading: iwiNameIsLoading } = useList({
    resource: 'iwi',
    pagination: { pageSize: 100 },
    filters: [
      {
        field: 'id',
        operator: 'eq',
        value: data?.data.iwi,
      },
    ],
    queryOptions: {
      enabled: data?.data.iwi && data?.data.iwi.length > 0 && !isFetching,
    },

    errorNotification: (error) => {
      return {
        message: 'Error getting iwi names',
        description:
          error?.message || 'An error occurred while fetching iwi names',
        type: 'error',
      }
    },
  })

  const isLoading =
    isFetching ||
    regionDataIsLoading ||
    funderAssignedToIsLoading ||
    (regionNameIsLoading &&
      regionData?.data &&
      regionData?.data.length > 0 &&
      !regionDataIsLoading) ||
    (iwiNameIsLoading &&
      data?.data.iwi &&
      data?.data.iwi.length > 0 &&
      !isFetching)

  if (isError) return <div>Error</div>

  return (
    <Show
      title={<Typography variant="h5">Back to Funders List</Typography>}
      isLoading={isLoading}
      key={data?.data.uuid}
    >
      <Box sx={{ borderBotton: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Funder Details" {...a11yProps(0)} />
          <Tab label="Revisions" {...a11yProps(1)} />
          <Tab label="Comments" {...a11yProps(2)} />
          <Tab label="Proposed Edits" {...a11yProps(3)} />
        </Tabs>
      </Box>

      {tabValue == 0 && (
        <Stack gap={1}>
          {assignUserModalOpen && (
            <AssignUserFunderModal
              open={assignUserModalOpen}
              onClose={() => setAssignUserModalOpen(false)}
              funder_uuid={data?.data.uuid}
              currentUser={user}
              currentAssignedUser={
                funderAssignedTo?.data?.assigned_to_user_uuid ?? ''
              }
            />
          )}

          <Typography variant="h5">{data?.data.name}</Typography>
          <Divider sx={{ mb: 1 }} />

          <Typography
            variant="body1"
            fontWeight="bold"
            alignSelf={'flex-start'}
          >
            Active
            {data?.data.active ? (
              <CheckCircleIcon color="success" fontSize="inherit" />
            ) : (
              <>
                <CancelIcon color="error" fontSize="inherit" />
                <br />
                Status:{' '}
                {data?.data.status ? data.data.status : '*No status available*'}
              </>
            )}
          </Typography>
          <AdminComments
            adminComments={data?.data.admin_comments}
            funderUuid={data?.data.uuid}
            opportunityUuid={null}
            onlyShowLatest={true}
          />
          {funderAssignedTo?.data && funderAssignedTo?.data.assigned_to_user ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {' '}
                Assigned to: {funderAssignedTo?.data.assigned_to_user}
              </Typography>
              <IconButton size="small" onClick={() => toggleAssignUserEdit()}>
                <EditIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {' '}
                Unassigned
              </Typography>
              <IconButton size="small" onClick={() => toggleAssignUserEdit()}>
                <AddIcon />
              </IconButton>
            </Box>
          )}

          {data?.data.next_check_in_date && (
            <>
              <Typography variant="body1" fontWeight="bold">
                Next Check-in Date
              </Typography>
              <Typography variant="body2">
                {new Date(data.data.next_check_in_date).toLocaleDateString(
                  'en-NZ',
                  {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  },
                )}
              </Typography>
            </>
          )}

          <Typography variant="body1" fontWeight="bold">
            Email Address
          </Typography>
          {data?.data.email_address ? (
            <Typography variant="body2">{data.data.email_address}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          )}

          <Typography variant="body1" fontWeight="bold">
            {' '}
            {data?.data.only_funds_registered_charities === true
              ? 'Only funds registered charities'
              : data?.data.only_funds_registered_charities === false
              ? 'Funds all charities'
              : null}
          </Typography>

          <Typography variant="body1" fontWeight="bold">
            {' '}
            Gambling Proceeds
          </Typography>
          <Typography variant="body2">
            {data?.data.gambling_proceeds ? 'Yes' : 'No'}
          </Typography>

          <Typography variant="body1" fontWeight="bold">
            {' '}
            Is International
          </Typography>
          <Typography variant="body2">
            {data?.data.is_international ? 'Yes' : 'No'}
          </Typography>

          <Typography variant="body1" fontWeight="bold">
            Iwi
          </Typography>

          {iwiNamesData?.data && iwiNamesData.data.length > 0 ? (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {iwiNamesData.data.map((iwi: any, index: number) => (
                  <Typography key={iwi.id}>
                    {iwi.name}
                    {index < iwiNamesData.data.length - 1 ? ',' : ''}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null}

          {/* No iwi message */}
          {(!iwiNamesData?.data || iwiNamesData.data.length === 0) && (
            <Typography variant="body1" color="text.secondary">
              No iwi specified
            </Typography>
          )}

          {data?.data.description ? (
            <>
              <Typography variant="body1" fontWeight="bold">
                {' '}
                Description
              </Typography>
              <ReactMarkdown
                className={`markdown-${lightOrDarkMode}`}
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              >
                {data?.data.description || '*No description provided*'}
              </ReactMarkdown>
            </>
          ) : (
            <>
              <Typography variant="body1" fontWeight="bold">
                {' '}
                Description <InfoIcon color="error" fontSize="inherit" />
              </Typography>
              <Typography variant="body2">*No description provided*</Typography>
            </>
          )}

          <Typography variant="body1" fontWeight="bold">
            {' '}
            Type
          </Typography>
          <Typography variant="body2">
            {data?.data.type || '*No type provided*'}
          </Typography>

          {data?.data.website ? (
            <>
              <Typography variant="body1" fontWeight="bold">
                Website
              </Typography>
              <Link
                href={data?.data.website}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: '16px' }}
              >
                {data?.data.website}
              </Link>
            </>
          ) : (
            <>
              <Typography variant="body1" fontWeight="bold">
                Website
              </Typography>
              <Typography variant="body2">*No website provided*</Typography>
            </>
          )}
          <Box display="flex" width={'100%'} justifyContent="flex-start">
            <Box display="flex" flexDirection="column" width={'100%'}>
              {regionData?.data &&
              regionNamesData?.data?.length !== undefined &&
              regionNamesData?.data.length > 0 &&
              !regionDataIsLoading ? (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    Locations
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    {regionNamesData?.data.map((region: any) => {
                      const hierarchy = [
                        region.country,
                        region.region,
                        region.council,
                        region.major,
                        region.suburb,
                      ]
                        .filter(Boolean)
                        .join(' → ')
                      return (
                        <Tooltip
                          key={region.id}
                          title={hierarchy || ''}
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '0.85rem',
                                bgcolor: 'white',
                                color: 'grey.700',
                                boxShadow: 1,
                                maxWidth: 400,
                              },
                            },
                          }}
                        >
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.preventDefault()
                              show('regions', region.id)
                            }}
                            sx={{
                              cursor: 'pointer',
                              textDecoration: 'none',
                              textAlign: 'left',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {region.name}
                          </Link>
                        </Tooltip>
                      )
                    })}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    Locations <InfoIcon color="error" fontSize="inherit" />
                  </Typography>
                  <Typography variant="body2">
                    *No location provided*
                  </Typography>
                </>
              )}
            </Box>

            <Box display="flex" width={'100%'} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={toggleRegionsEdit}
                sx={{ marginLeft: '16px', maxHeight: '40px' }}
              >
                {editingRegions ? 'Close' : 'Manage Funder Locations'}
              </Button>
            </Box>
          </Box>
          {data?.data.uuid && editingRegions && (
            <Box bgcolor={'#f5f5f5'} borderRadius={4} p={2}>
              <Typography variant="h5" gutterBottom>
                Manage Locations for {data?.data.name}
              </Typography>
              <FunderRegionManagement
                funderUuid={data?.data.uuid}
                regionData={regionData}
                regionNamesData={regionNamesData}
                regionDataIsLoading={regionDataIsLoading}
              />
            </Box>
          )}

          <Typography variant="body1" fontWeight="bold">
            Notes
          </Typography>

          <ReactMarkdown
            className={`markdown-${lightOrDarkMode}`}
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {data?.data.notes || '*No notes provided*'}
          </ReactMarkdown>

          <Box
            display="flex"
            flexDirection={'column'}
            width={'100%'}
            justifyContent="space-between"
          >
            <Box>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="h5">Opportunities</Typography>
              </Box>
              <FunderOpportunitiesList
                funderUuid={data?.data.uuid}
                setOpenUpdateProfilesModal={setOpenUpdateProfilesModal}
                setOpportunityUUIDsToUpdate={setOpportunityUUIDsToUpdate}
              />
              {openUpdateProfilesModal && (
                <UpdateProfilesModal
                  funderUuid={data?.data.uuid}
                  open={openUpdateProfilesModal}
                  onClose={() => setOpenUpdateProfilesModal(false)}
                  opportunityUUIDsToUpdate={opportunityUUIDsToUpdate}
                />
              )}

              {!addingOpportunity && (
                <Button
                  variant="contained"
                  onClick={toggleAddingOpportunity}
                  sx={{ mt: 2 }}
                >
                  Add Opportunity
                </Button>
              )}
            </Box>

            {addingOpportunity && addMode === 'name' && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Fund name"
                    value={newFundName}
                    onChange={(e) => setNewFundName(e.target.value)}
                    size="small"
                    sx={{ minWidth: 300 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddOpportunity()
                    }}
                    autoFocus
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddOpportunity}
                    disabled={!newFundName.trim() || isCreatingOpportunity}
                    size="small"
                  >
                    {isCreatingOpportunity ? (
                      <CircularProgress size={20} />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAddingOpportunity(false)
                      setNewFundName('')
                    }}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
                <Link
                  component="button"
                  type="button"
                  onClick={() => setAddMode('references')}
                  sx={{ mt: 1, alignSelf: 'flex-start', fontSize: '0.875rem' }}
                >
                  Add with references and auto-fill →
                </Link>
              </Box>
            )}

            {addingOpportunity && addMode === 'references' && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Paste reference URLs (one per line). The create form will open
                  with auto-fill running — fund name and other details are
                  extracted from the references.
                </Typography>
                <TextField
                  label="References"
                  value={newReferences}
                  onChange={(e) => setNewReferences(e.target.value)}
                  size="small"
                  multiline
                  rows={4}
                  sx={{ minWidth: 400 }}
                  autoFocus
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenCreateWithReferences}
                    disabled={!newReferences.trim()}
                    size="small"
                  >
                    Extract and create
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAddingOpportunity(false)
                      setNewReferences('')
                      setAddMode('name')
                    }}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
                <Link
                  component="button"
                  type="button"
                  onClick={() => setAddMode('name')}
                  sx={{ mt: 1, alignSelf: 'flex-start', fontSize: '0.875rem' }}
                >
                  ← Back to quick add
                </Link>
              </Box>
            )}
          </Box>
        </Stack>
      )}
      {tabValue == 1 &&
        data?.data.uuid &&
        !isFetching &&
        !regionDataIsLoading && <FunderRevisions funderUUID={data.data.uuid} />}

      {tabValue == 2 && data?.data.uuid && (
        <AdminComments
          adminComments={data?.data.admin_comments}
          funderUuid={data?.data.uuid}
          opportunityUuid={null}
        />
      )}

      {tabValue == 3 && data?.data.uuid && (
        <ProposedEditsPanel funderUuid={data.data.uuid} />
      )}
    </Show>
  )
}
